import type { QueryClient } from '@tanstack/react-query';
import { queryClient as appQueryClient } from '@/lib/queryClient';
import { restaurantKeys } from '@/api/keys/restaurantKeys';
import type { Restaurant } from '@/types/restaurant';

/**
 * `dataUpdatedAt` stamped on a list-derived seed. React Query's own "never
 * fetched" value: `timeUntilStale(0, staleTime)` is always 0, so the entry is
 * stale under any `staleTime` and `useQuery` refetches it on mount
 * (`refetchOnMount` defaults to true).
 */
export const SEEDED_DETAIL_UPDATED_AT = 0;

/**
 * A list/search item: `Restaurant` plus the fields that must not reach the
 * detail cache. `_calculatedDistance` is client-only; `hasScore` is a
 * search-payload field that is not on the `Restaurant` type.
 */
type SeedableRestaurant = Restaurant & { _calculatedDistance?: number | null; hasScore?: boolean };

/**
 * Lets the profile open at once from data a restaurant card already holds.
 * Call through `seedRestaurantProfileIfComplete` unless the payload is already
 * known to be a complete `Restaurant`.
 *
 * The search list and `GET /restaurants/{id}/summary` are built from the same
 * backend `RestaurantSummary` (same business-config pass), so a list item is a
 * near-complete detail payload. The only fields that differ: `sharedNotes`
 * (`[]` in search, but list-details stamps the list owner's or viewer's note
 * there; the summary returns the member's own shared notes, so the seed always
 * writes `[]`) and
 * `cuisineTypes[].learnMoreToken` (summary-only). Both are handled by the page
 * (shared notes render once the summary lands; the token is optional and arrives
 * with the refetch). Never overwrites an existing detail entry: a profile the member
 * already viewed has real `sharedNotes` and must keep them.
 *
 * The per-tier score fields (`hasScore`, `scoreSource`, `rankingTierReason`,
 * `affinityScore`) are deliberately NOT seeded. Search stamps them per ranking
 * tier/context, so the list values are not authoritative for the profile, and
 * `/summary` computes its own per-member values (e.g. `scoreSource` of
 * `self_rating` or `taste_match`) that would flip the display when it lands.
 * The profile reads only `rating`, directly or via `hasPersonalRating`, which
 * the list and summary agree on, so leaving the score fields undefined makes
 * every reader fall back to that `rating != null` logic. Distance is not
 * rendered on the profile and the summary never carries it, so nothing needs
 * to survive the replace.
 *
 * Uses the app `queryClient` singleton (the same instance `App` hands to both
 * `QueryClientProvider` and the router context) so cards need no provider.
 *
 * After this call the detail cache always holds data for the restaurant, which
 * is what lets the route loader skip awaiting the network.
 */
export function seedRestaurantProfile(
  restaurant: SeedableRestaurant,
  client: QueryClient = appQueryClient,
): void {
  const key = restaurantKeys.detail(restaurant.id);
  if (client.getQueryData(key) !== undefined) return;

  const {
    // List-scoped, never profile-scoped: `/lists/{urlKey}/details/` stamps the
    // list owner's (or viewer's) note here, which is not what `/summary`
    // returns. Forced to `[]` below; the real notes arrive with the refetch.
    sharedNotes: _sharedNotes,
    _calculatedDistance: _distance,
    hasScore: _hasScore,
    scoreSource: _scoreSource,
    rankingTierReason: _rankingTierReason,
    affinityScore: _affinityScore,
    ...detail
  } = restaurant;
  void [_sharedNotes, _distance, _hasScore, _scoreSource, _rankingTierReason, _affinityScore];
  client.setQueryData<Restaurant>(key, { ...detail, sharedNotes: [] }, { updatedAt: SEEDED_DETAIL_UPDATED_AT });
}

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null;

/**
 * Runtime guard: does `value` carry everything the profile hero and body need
 * to render the SAME thing the summary would, or whose absence would render
 * WRONG data rather than nothing? Partial payloads (feed items, notifications,
 * autocomplete rows, collaborative-list items) fail it and must never reach the
 * detail cache.
 *
 * Derived from RestaurantProfile and its children:
 * - `id`, `name`, `address`, `images`: hero, info card, SEO/JSON-LD.
 * - `hours` is `null` or an object. A restaurant freshly ingested by search
 *   (`bulk_create(... hours=opening_hours if opening_hours else None)` in
 *   `nearby_search_service._bulk_create_restaurants`, which skips `save()` and
 *   its `None -> {}` normalisation) legitimately has no hours yet. Every profile
 *   read is optional-chained (`HoursSection` takes `hours?: Hours` and checks
 *   `hours?.weekdayText`), so `null` renders "no hours" exactly as the summary
 *   would. Rejecting it would only make brand-new restaurants take the slow
 *   cold path. A missing key or a non-object still fails.
 * - `geometry` is an object. The API always emits one (`{}` when the place had
 *   no coordinates) and every profile/child read is `geometry?.location?.…` or
 *   guarded by an `if (geometry?.location)` (`restaurantJsonLd`,
 *   `useRestaurantCheckIn`, `NearbyMapPreview`, `ActionButtons`), so a missing
 *   `location` degrades to "no map/directions", never to wrong data.
 * - `vibes` (always emitted by the API): the About section.
 * - `rating`, `googleRating`, `priceLevel` present (nullable): an absent key
 *   would hide the TasteMatch pill / price until the refetch.
 * - `selfRated`, `isBookmarked` booleans and `notes` string-or-null (anonymous
 *   summaries emit null): per-member state; missing would show the wrong state,
 *   and `RestaurantNotes` captures `initialNotes` once at mount.
 * `proPhotos` is deliberately not required: the API only emits it for
 * business-config outlets and the profile already falls back to `[]`.
 */
export function isSeedableRestaurant(value: unknown): value is SeedableRestaurant {
  if (!isRecord(value)) return false;
  return (
    typeof value.id === 'number' &&
    typeof value.name === 'string' &&
    typeof value.address === 'string' &&
    (value.hours === null || isRecord(value.hours)) &&
    isRecord(value.geometry) &&
    Array.isArray(value.images) &&
    Array.isArray(value.vibes) &&
    value.rating !== undefined &&
    value.googleRating !== undefined &&
    value.priceLevel !== undefined &&
    typeof value.selfRated === 'boolean' &&
    typeof value.isBookmarked === 'boolean' &&
    (value.notes === null || typeof value.notes === 'string')
  );
}

/**
 * Seeds only when `payload` passes `isSeedableRestaurant`. Returns whether the
 * payload was seedable (it does not report whether an existing entry already
 * won; callers needing "is data cached" read the cache).
 */
export function seedRestaurantProfileIfComplete(
  payload: unknown,
  client: QueryClient = appQueryClient,
): boolean {
  if (!isSeedableRestaurant(payload)) return false;
  seedRestaurantProfile(payload, client);
  return true;
}
